import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { useSettings } from "@/context/SettingsContext";
import { api, formatFCFA, formatApiError } from "@/lib/api";
import { trackPurchase } from "@/lib/pixel";
import { toast } from "sonner";

const COUNTRY_CODES = [
  { code: "+225", flag: "🇨🇮", label: "Côte d'Ivoire" },
  { code: "+221", flag: "🇸🇳", label: "Sénégal" },
  { code: "+229", flag: "🇧🇯", label: "Bénin" },
  { code: "+228", flag: "🇹🇬", label: "Togo" },
  { code: "+226", flag: "🇧🇫", label: "Burkina Faso" },
  { code: "+223", flag: "🇲🇱", label: "Mali" },
  { code: "+227", flag: "🇳🇪", label: "Niger" },
  { code: "+237", flag: "🇨🇲", label: "Cameroun" },
  { code: "+33", flag: "🇫🇷", label: "France" },
  { code: "+1", flag: "🇺🇸", label: "USA / Canada" },
];

export default function Checkout() {
  const { items, subtotal, clearCart } = useCart();
  const { settings } = useSettings();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    customer_name: "",
    countryCode: "+225",
    phone: "",
    address: "",
    commune: "",
    remark: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const zones = settings?.delivery_zones || [];

  // Aggregate communes with their zone meta
  const communeOptions = useMemo(() => {
    const opts = [];
    for (const z of zones) {
      for (const c of z.cities || []) {
        opts.push({ commune: c, zoneName: z.name, fee: z.fee });
      }
    }
    return opts.sort((a, b) => a.commune.localeCompare(b.commune));
  }, [zones]);

  const selectedZone = communeOptions.find((c) => c.commune === form.commune);
  const isFreeZone = form.commune === "__autre__";
  const deliveryFee = selectedZone
    ? selectedZone.fee
    : isFreeZone
    ? settings?.free_zone_fee || 5000
    : 0;
  const zoneName = selectedZone
    ? selectedZone.zoneName
    : isFreeZone
    ? "Zone 4 (autre destination)"
    : "";
  const total = subtotal + deliveryFee;

  if (items.length === 0) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-32 text-center">
        <p className="text-[#6B6B6B]">Votre panier est vide.</p>
        <button onClick={() => navigate("/catalogue")} className="btn-primary mt-6">
          Voir le catalogue
        </button>
      </div>
    );
  }

  const fullPhone = `${form.countryCode} ${form.phone}`.trim();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.customer_name || !form.phone || !form.address || !form.commune) {
      toast.error("Veuillez remplir tous les champs obligatoires");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        customer_name: form.customer_name,
        phone: fullPhone,
        address: form.address,
        city: form.commune === "__autre__" ? "Autre destination" : form.commune,
        remark: form.remark,
        items: items.map((i) => ({
          product_id: i.product_id,
          name: i.name,
          variant_name: i.variant_name,
          variant_id: i.variant_id,
          quantity: i.quantity,
          price: i.price,
          cost_price: i.cost_price || 0,
        })),
        subtotal,
        delivery_fee: deliveryFee,
        total,
        delivery_zone: zoneName,
      };
      const res = await api.post("/orders", payload);
      const order = res.data;

      trackPurchase({ items, value: total, order_number: order.order_number });

      const number = settings?.whatsapp_number || "2250711693161";
      const productsText = items
        .map((i) => `• ${i.name}${i.variant_name ? ` (${i.variant_name})` : ""} x${i.quantity} – ${formatFCFA(i.price * i.quantity)}`)
        .join("\n");
      const msg =
        `Commande Refuge de la Foi (${order.order_number})\n\n` +
        `Nom : ${form.customer_name}\n` +
        `Téléphone : ${fullPhone}\n` +
        `Adresse précise : ${form.address}\n` +
        `Commune : ${payload.city}\n\n` +
        `Produits :\n${productsText}\n\n` +
        `Livraison : ${formatFCFA(deliveryFee)} (${zoneName})\n` +
        `Total : ${formatFCFA(total)}\n\n` +
        (form.remark ? `Remarque : ${form.remark}` : "");

      window.open(`https://wa.me/${number}?text=${encodeURIComponent(msg)}`, "_blank");
      toast.success("Commande envoyée");
      clearCart();
      navigate("/");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-10 py-16 lg:py-24">
      <div className="label-tiny mb-3 flex items-center gap-3">
        <span className="gold-line" /> Validation
      </div>
      <h1 className="font-display text-4xl lg:text-5xl tracking-tight mb-12">Finaliser la commande</h1>

      <div className="grid lg:grid-cols-[1fr_400px] gap-10">
        <form onSubmit={handleSubmit} className="space-y-5" data-testid="checkout-form">
          <div>
            <label className="label-tiny block mb-1">Nom complet *</label>
            <input
              className="input-gold"
              value={form.customer_name}
              onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
              required
              data-testid="checkout-name"
            />
          </div>

          <div>
            <label className="label-tiny block mb-1">Téléphone *</label>
            <div className="flex gap-2">
              <select
                className="input-gold !w-auto pr-8"
                value={form.countryCode}
                onChange={(e) => setForm({ ...form, countryCode: e.target.value })}
                data-testid="checkout-country-code"
                style={{ minWidth: "120px" }}
              >
                {COUNTRY_CODES.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.flag} {c.code}
                  </option>
                ))}
              </select>
              <input
                className="input-gold flex-1"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value.replace(/[^\d\s]/g, "") })}
                required
                placeholder="07 11 69 31 61"
                data-testid="checkout-phone"
              />
            </div>
          </div>

          <div>
            <label className="label-tiny block mb-1">Adresse précise *</label>
            <input
              className="input-gold"
              value={form.address}
              onChange={(e) => setForm({ ...form, address: e.target.value })}
              required
              placeholder="Rue, quartier, résidence, repère..."
              data-testid="checkout-address"
            />
          </div>

          <div>
            <label className="label-tiny block mb-1">Commune de livraison *</label>
            <select
              className="input-gold"
              value={form.commune}
              onChange={(e) => setForm({ ...form, commune: e.target.value })}
              required
              data-testid="checkout-commune"
            >
              <option value="">— Choisir une commune —</option>
              {zones.map((z) => (
                <optgroup key={z.name} label={`${z.name} (${formatFCFA(z.fee)})`}>
                  {(z.cities || []).map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </optgroup>
              ))}
              <option value="__autre__">{`Autre destination (${formatFCFA(settings?.free_zone_fee || 5000)})`}</option>
            </select>
            {form.commune && (
              <p className="text-xs text-[#6B6B6B] mt-2" data-testid="checkout-zone-info">
                {zoneName} – Frais {formatFCFA(deliveryFee)}
              </p>
            )}
          </div>

          <div>
            <label className="label-tiny block mb-1">Remarque</label>
            <textarea
              className="input-gold min-h-[100px]"
              value={form.remark}
              onChange={(e) => setForm({ ...form, remark: e.target.value })}
              data-testid="checkout-remark"
            />
          </div>

          <button type="submit" className="btn-primary w-full" disabled={submitting} data-testid="checkout-submit-btn">
            {submitting ? "Envoi..." : "Confirmer & envoyer via WhatsApp"}
          </button>
          <p className="text-xs text-[#6B6B6B] text-center italic">
            Paiement à la livraison uniquement.
          </p>
        </form>

        <div className="border border-[#B8860B]/30 bg-white p-8 h-fit">
          <h3 className="font-display text-xl text-[#B8860B] mb-6">Votre commande</h3>
          <div className="space-y-3 mb-6 max-h-72 overflow-y-auto">
            {items.map((i) => (
              <div key={i.key} className="flex justify-between text-sm">
                <span className="truncate pr-3">{i.name} x{i.quantity}</span>
                <span className="text-[#B8860B] flex-shrink-0">{formatFCFA(i.price * i.quantity)}</span>
              </div>
            ))}
          </div>
          <div className="border-t border-black/10 pt-4 space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-[#6B6B6B]">Sous-total</span><span>{formatFCFA(subtotal)}</span></div>
            <div className="flex justify-between"><span className="text-[#6B6B6B]">Livraison</span><span data-testid="checkout-delivery-fee">{formatFCFA(deliveryFee)}</span></div>
            <div className="flex justify-between text-lg pt-2 border-t border-black/10 mt-3">
              <span className="font-display">Total</span>
              <span className="text-[#B8860B] font-display" data-testid="checkout-total">{formatFCFA(total)}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
